import axios from 'axios';
import { getAuthHeader } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

export interface LearningCategory {
  id: number;
  name: string;
  description?: string;
  moduleCount?: number;
}

export interface LearningModule {
  id: number;
  title: string;
  description: string;
  content: string;
  categoryId: number;
  categoryName?: string;
  tags?: string[];
  difficultyLevel?: string;
  estimatedMinutes?: number;
  viewCount?: number;
  isCompleted?: boolean;
  completedAt?: string;
  createdAt: string;
  updatedAt?: string;
  authorName?: string;
}

export interface LearningModuleResponse {
  content: LearningModule[];
  totalPages: number;
  totalElements: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export const learningService = {
  // Get all categories
  getCategories: async (): Promise<LearningCategory[]> => {
    const response = await axios.get(`${API_BASE_URL}/learning/categories`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Get learning modules with pagination and filters
  getModules: async (
    page: number = 0,
    size: number = 20,
    categoryId?: number,
    search?: string,
    difficultyLevel?: string
  ): Promise<LearningModuleResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });

    if (categoryId) {
      params.append('categoryId', categoryId.toString());
    }
    if (search) {
      params.append('search', search);
    }
    if (difficultyLevel) {
      params.append('difficultyLevel', difficultyLevel);
    }

    const response = await axios.get(`${API_BASE_URL}/learning/modules?${params}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Get single module details
  getModule: async (id: number): Promise<LearningModule> => {
    const response = await axios.get(`${API_BASE_URL}/learning/modules/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Mark module as completed (for students)
  completeModule: async (moduleId: number): Promise<void> => {
    await axios.post(`${API_BASE_URL}/learning/modules/${moduleId}/complete`, {}, {
      headers: getAuthHeader()
    });
  },

  // Create a new category (admin/provider only)
  createCategory: async (name: string, description?: string): Promise<LearningCategory> => {
    const params = new URLSearchParams({ name });
    if (description) {
      params.append('description', description);
    }

    const response = await axios.post(`${API_BASE_URL}/learning/categories?${params}`, {}, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Create a new module (admin/provider only)
  createModule: async (module: Partial<LearningModule>): Promise<LearningModule> => {
    const response = await axios.post(`${API_BASE_URL}/learning/modules`, module, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Update a module (admin/provider only)
  updateModule: async (id: number, module: Partial<LearningModule>): Promise<LearningModule> => {
    const response = await axios.put(`${API_BASE_URL}/learning/modules/${id}`, module, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  // Delete a module (admin only)
  deleteModule: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/learning/modules/${id}`, {
      headers: getAuthHeader()
    });
  }
};